import React, { useEffect, useRef, useState } from 'react';
import { SignInButton, SignOutButton, useAuth, useUser } from '@clerk/clerk-react';
import HireRight from '../assets/pdf/certificates/HireRight_Certificate.pdf';
import Luxoft from '../assets/pdf/certificates/Luxoft_Certificate.pdf';
import Seavus from '../assets/pdf/certificates/Seavus_Certificate.pdf';
import ReactMimo from '../assets/pdf/certificates/React_Certificate.pdf';
import TSMimo from '../assets/pdf/certificates/TS_Certificate.pdf';
import CV from '../assets/pdf/cv/Andrei Karcheuski CV.pdf';
import Logo from '../assets/logo.png';
import styles from './CustomNavbar.module.css';
import ButtonWithArrow from '../components/ButtonWithArrow';

interface CustomNavbar {
    setContactOpen: any;
    isSmallScreen: boolean;
    onNavigate?: (page: 'home' | 'accent-detector' | 'video-generator') => void;
}

function Dropdown({ title, children }) {
    const [open, setOpen] = useState(false);
    const closeTimeoutRef = useRef<number | undefined>(undefined);
    // Use click to toggle for drawer, hover for navbar
    const isDrawer = !!(typeof window !== 'undefined' && window.innerWidth < 768);
    const handleToggle = () => setOpen((prev) => !prev);

    const openMenu = () => {
        if (closeTimeoutRef.current) {
            window.clearTimeout(closeTimeoutRef.current);
            closeTimeoutRef.current = undefined;
        }
        setOpen(true);
    };

    const scheduleClose = () => {
        if (closeTimeoutRef.current) {
            window.clearTimeout(closeTimeoutRef.current);
        }
        closeTimeoutRef.current = window.setTimeout(() => {
            setOpen(false);
            closeTimeoutRef.current = undefined;
        }, 240);
    };

    useEffect(() => {
        return () => {
            if (closeTimeoutRef.current) {
                window.clearTimeout(closeTimeoutRef.current);
            }
        };
    }, []);

    return (
        <div
            className={styles.dropdown}
            onMouseEnter={!isDrawer ? openMenu : undefined}
            onMouseLeave={!isDrawer ? scheduleClose : undefined}
        >
            <button
                className={
                    isDrawer
                        ? styles.drawerDropdownToggle
                        : `${styles.dropdownToggle} ${open ? styles.dropdownToggleActive : ''}`
                }
                onClick={isDrawer ? handleToggle : undefined}
                type="button"
                aria-expanded={open}
            >
                <span style={{ position: 'relative', display: 'inline-block' }}>
                    {title}
                    {/* Arrowhead for large screens only */}
                    {!isDrawer && (
                        <span className={styles.dropdownToggle__arrow} aria-hidden="true">
                            <svg width="18" height="18" viewBox="0 0 18 18" style={{ display: 'inline-block' }}>
                                <polyline points="4,7 9,12 14,7" fill="none" stroke="#004aaa" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        </span>
                    )}
                    {/* Underline animation for large screens only */}
                    {!isDrawer && (
                        <span className={styles.dropdownToggle__underline} />
                    )}
                </span>
            </button>
            {open && (
                <div className={isDrawer ? styles.drawerDropdownMenu : styles.dropdownMenu}>
                    {children}
                </div>
            )}
        </div>
    );
}

const CustomNavbar: React.FC<CustomNavbar> = ({ setContactOpen, isSmallScreen, onNavigate }) => {
    const [visible, setVisible] = useState(true);
    const [lastScroll, setLastScroll] = useState(0);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [drawerClosing, setDrawerClosing] = useState(false);
    const [userMenuOpen, setUserMenuOpen] = useState(false);
    const userMenuCloseTimeout = useRef<number | undefined>(undefined);
    // Local state for hamburger/cross icon
    const [hamburgerOpen, setHamburgerOpen] = useState(false);
    const { isSignedIn } = useAuth();
    const { user, isLoaded } = useUser();
    const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
    const fileInputRef = useRef<HTMLInputElement | null>(null);
    const userMenuRef = useRef<HTMLDivElement | null>(null);
    const displayEmail = user?.primaryEmailAddress?.emailAddress || user?.emailAddresses?.[0]?.emailAddress || '';
    const displayName = user?.firstName || user?.username || displayEmail || 'User';
    const avatarInitial = displayName?.charAt(0).toUpperCase() || 'U';

    const handleAvatarUploadClick = () => {
        fileInputRef.current?.click();
    };

    const handleAvatarFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            setAvatarUrl(URL.createObjectURL(file));
        }
    };

    const closeDrawer = (delay = 100) => {
        setHamburgerOpen(false);
        setDrawerClosing(true);
        window.setTimeout(() => {
            setDrawerOpen(false);
            setDrawerClosing(false);
        }, delay);
    };

    const handleNavigate = (page: 'home' | 'accent-detector' | 'video-generator', event?: React.MouseEvent<HTMLElement>) => {
        event?.preventDefault();
        onNavigate?.(page);

        if (drawerOpen) {
            closeDrawer(140);
        }
    };

    const openUserMenu = () => {
        if (userMenuCloseTimeout.current) {
            window.clearTimeout(userMenuCloseTimeout.current);
            userMenuCloseTimeout.current = undefined;
        }
        setUserMenuOpen(true);
    };

    const closeUserMenu = () => {
        if (userMenuCloseTimeout.current) {
            window.clearTimeout(userMenuCloseTimeout.current);
        }
        userMenuCloseTimeout.current = window.setTimeout(() => {
            setUserMenuOpen(false);
            userMenuCloseTimeout.current = undefined;
        }, 50);
    };

    const toggleUserMenu = () => {
        if (userMenuOpen) {
            setUserMenuOpen(false);
        } else {
            openUserMenu();
            setDrawerOpen(false);
            setDrawerClosing(false);
            setHamburgerOpen(false);
        }
    };

    useEffect(() => {
        const handleScroll = () => {
            const currentScroll = window.scrollY;
            if (currentScroll > lastScroll && currentScroll > 80) {
                setVisible(false);
            } else {
                setVisible(true);
            }
            setLastScroll(currentScroll);
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, [lastScroll]);

    useEffect(() => {
        if (drawerOpen && userMenuOpen) {
            if (userMenuCloseTimeout.current) {
                window.clearTimeout(userMenuCloseTimeout.current);
                userMenuCloseTimeout.current = undefined;
            }
            setUserMenuOpen(false);
        }
    }, [drawerOpen, userMenuOpen]);

    // Animated Hamburger/Cross icon button (centered, morphs smoothly)
    const DrawerToggleIcon = ({ open }) => (
        <button
            className={`${styles.hamburgerButton} ${open ? styles.hamburgerOpen : ''}`}
            aria-label={open ? 'Close menu' : 'Open menu'}
            onClick={() => {
                if (open) {
                    setHamburgerOpen(false); // morph to hamburger
                    setDrawerClosing(true);
                    setTimeout(() => {
                        setDrawerOpen(false);
                        setDrawerClosing(false);
                    }, 100);
                } else {
                    if (userMenuCloseTimeout.current) {
                        window.clearTimeout(userMenuCloseTimeout.current);
                        userMenuCloseTimeout.current = undefined;
                    }
                    setUserMenuOpen(false);
                    setHamburgerOpen(true); // morph to cross
                    setDrawerOpen(true);
                }
            }}
            type="button"
        >
            <span className={styles.hamburgerIcon}>
                <span className={styles.hamburgerLine} />
                <span className={styles.hamburgerLine} />
                <span className={styles.hamburgerLine} />
            </span>
        </button>
    );

    useEffect(() => {
        const handleDocumentClick = (event: MouseEvent) => {
            if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
                setUserMenuOpen(false);
            }
        };

        window.addEventListener('click', handleDocumentClick);
        return () => {
            window.removeEventListener('click', handleDocumentClick);
            if (userMenuCloseTimeout.current) {
                window.clearTimeout(userMenuCloseTimeout.current);
            }
        };
    }, []);

    // Drawer menu for small screens (no header/logo, just links)
    const DrawerMenu = () => (
        <div
            className={
                `${styles.drawer} ${drawerOpen && !drawerClosing ? styles.drawerOpen : ''} ${drawerClosing ? styles.drawerClose : ''}`
            }
        >
            <div className={styles.drawerContent}>
                <div className={styles.drawerLinksCard}>
                    <div className={styles.drawerLinksHeading}>Menu</div>
                    <div className={styles.drawerLinks}>
                        <Dropdown title="Solutions">
                            <a onClick={() => { setContactOpen(true); closeDrawer(500); }} className={styles.dropdownItem}>AI-Agents</a>
                            <a onClick={() => { setContactOpen(true); closeDrawer(500); }} className={styles.dropdownItem}>Applications Development</a>
                        </Dropdown>
                        <Dropdown title="Products">
                            <a href="/video-generation" onClick={(event) => handleNavigate('video-generator', event)} className={styles.dropdownItem}>⚽ PhotoMotion</a>
                            <a href="/accent-detector" onClick={(event) => handleNavigate('accent-detector', event)} className={styles.dropdownItem}>🎤 Accent Detector</a>
                        </Dropdown>
                        <Dropdown title="Meet Andrei">
                            <a href="https://www.linkedin.com/in/karcheuski" target="_blank" rel="noopener" className={styles.dropdownItem}>Linkedin</a>
                            <a href="https://stackoverflow.com/users/1015577/andrei-karcheuski" target="_blank" rel="noopener" className={styles.dropdownItem}>Stack Overflow</a>
                            <a href="https://github.com/akarcheuski" target="_blank" rel="noopener" className={styles.dropdownItem}>GitHub</a>
                            <div className={styles.drawerDivider} />
                            <a href={Luxoft} target="_blank" rel="noopener" className={styles.dropdownItem}>Luxoft Certificate</a>
                            <a href={HireRight} target="_blank" rel="noopener" className={styles.dropdownItem}>Hireright Certificate</a>
                            <a href={Seavus} target="_blank" rel="noopener" className={styles.dropdownItem}>Seavus Certificate</a>
                            <a href="https://www.englishscore.com/verify" target="_blank" rel="noopener" className={styles.dropdownItem}>English Score, ID: 357dcbb4a083</a>
                            <div className={styles.drawerDivider} />
                            <a href={CV} download="Andrei Karcheuski CV" className={styles.dropdownItem}>Download CV</a>
                        </Dropdown>
                    </div>
                </div>
            </div>
        </div>
    );

    return (
        <nav className={`${styles.navbar} ${visible ? styles.navbarVisible : styles.navbarHidden}`}>
            <div className={styles.container}
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-around',
                    width: '100%',
                    margin: '5px 0'
                }}>
                <a href="#home" className={styles.brand}
                    onClick={(event) => handleNavigate('home', event)}
                    style={{
                        position: 'relative',
                        left: 0,
                        right: 0,
                        // marginLeft: 'auto',
                        marginRight: 'auto',
                        display: 'flex',
                        // justifyContent: isSmallScreen ? 'center' : 'flex-start',
                        justifyContent: 'flex-start',
                        pointerEvents: 'auto',
                        zIndex: 1200,
                        cursor: 'pointer'
                    }}>
                    <div className={styles.appLogo}>
                        <img className={styles.starIcon} src={Logo} alt="logo" />
                        <svg width="150" height="35" viewBox="0 0 150 35">
                            <text x="0" y="25" className={styles.appLogoText}>AstroStar</text>
                        </svg>
                    </div>
                </a>
                {isSmallScreen && (
                    <div className={`${styles.mobileAuthGroup} ${styles.userMenuWrapper}`} ref={userMenuRef}>
                        {!isLoaded ? null : !isSignedIn ? (
                            <SignInButton mode="modal">
                                {/* <button type="button" className={styles.mobileSignInButton}>
                                        Sign in
                                    </button> */}
                                <button
                                    type="button"
                                    className={styles.userIconButton}
                                    aria-label="Open account menu"
                                    onClick={toggleUserMenu}
                                >
                                    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                                        <path d="M12 12c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm0 2c-3.33 0-10 1.67-10 5v3h20v-3c0-3.33-6.67-5-10-5z" />
                                    </svg>
                                </button>
                            </SignInButton>
                        ) : (
                            <button
                                type="button"
                                className={styles.userSignedIconButton}
                                aria-label="Open account menu"
                                onClick={toggleUserMenu}
                            >
                                <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                                    <path d="M12 12c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm0 2c-3.33 0-10 1.67-10 5v3h20v-3c0-3.33-6.67-5-10-5z" />
                                </svg>
                            </button>
                        )}

                        {userMenuOpen && isSignedIn && (
                            <div className={`${styles.userMenuDropdown} ${styles.mobileUserMenuDropdown}`}>
                                <span className={styles.userMenuEmail}>{displayEmail}</span>
                                <button
                                    type="button"
                                    className={styles.userMenuAvatarButton}
                                    onClick={handleAvatarUploadClick}
                                    aria-label="Upload avatar"
                                >
                                    {avatarUrl ? (
                                        <img src={avatarUrl} alt="User avatar" className={styles.userMenuAvatarImage} />
                                    ) : (
                                        <span className={styles.userMenuAvatarInitial}>{avatarInitial}</span>
                                    )}
                                </button>
                                <span className={styles.userMenuName}> Hi, {displayName}</span>
                                <div className={styles.userMenuDivider} />
                                <div className={styles.userMenuFooter}>
                                    <SignOutButton>
                                        <button type="button" className={styles.userMenuSignOutButton}>
                                            Sign out
                                        </button>
                                    </SignOutButton>
                                </div>
                            </div>
                        )}
                    </div>
                )}
                <div style={{ width: 48, display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                    {isSmallScreen && (
                        <DrawerToggleIcon open={hamburgerOpen} />
                    )}
                </div>
                {!isSmallScreen && (
                    <div className={styles.flexNavRow}>
                        <div className={styles.navLinks}>
                            <Dropdown title="Solutions">
                                <a onClick={() => setContactOpen(true)} target="_blank" rel="noopener" className={styles.dropdownItem}>AI-Agents</a>
                                <a onClick={() => setContactOpen(true)} target="_blank" rel="noopener" className={styles.dropdownItem}>Applications Development</a>
                            </Dropdown>
                            <Dropdown title="Products">
                                <a href="/video-generation" onClick={(event) => handleNavigate('video-generator', event)} className={styles.dropdownItem}>⚽ PhotoMotion</a>
                                <a href="/accent-detector" onClick={(event) => handleNavigate('accent-detector', event)} className={styles.dropdownItem}>🎤 Accent Detector</a>
                            </Dropdown>
                            <Dropdown title="Meet Andrei">
                                <a href="https://www.linkedin.com/in/karcheuski" target="_blank" rel="noopener" className={styles.dropdownItem}>Linkedin</a>
                                <a href="https://stackoverflow.com/users/1015577/andrei-karcheuski" target="_blank" rel="noopener" className={styles.dropdownItem}>Stack Overflow</a>
                                <a href="https://github.com/akarcheuski" target="_blank" rel="noopener" className={styles.dropdownItem}>GitHub</a>
                                <div className={styles.dropdownDivider} />
                                <a href={Luxoft} target="_blank" rel="noopener" className={styles.dropdownItem}>Luxoft Certificate</a>
                                <a href={HireRight} target="_blank" rel="noopener" className={styles.dropdownItem}>Hireright Certificate</a>
                                <a href={Seavus} target="_blank" rel="noopener" className={styles.dropdownItem}>Seavus Certificate</a>
                                <a href="https://www.englishscore.com/verify" target="_blank" rel="noopener" className={styles.dropdownItem}>English Score, ID: 357dcbb4a083</a>
                                <div className={styles.drawerDivider} />
                                <a href={CV} download="Andrei Karcheuski CV" className={styles.dropdownItem}>Download CV</a>
                            </Dropdown>
                        </div>
                        <div className={styles.getStarted} style={{ display: 'flex', alignItems: 'center', gap: '10px', position: 'relative' }}>
                            {!isLoaded ? null : !isSignedIn ? (
                                <SignInButton mode="modal">
                                    <button type="button" className={styles.authLinkButton}>
                                        <svg className={styles.authIcon} viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                                            <path d="M12 12c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm0 2c-3.33 0-10 1.67-10 5v3h20v-3c0-3.33-6.67-5-10-5z" />
                                        </svg>&nbsp;
                                        Sign in
                                    </button>
                                </SignInButton>
                            ) : (
                                <>
                                    <div
                                        className={styles.userMenuWrapper}
                                        ref={userMenuRef}
                                        onMouseEnter={openUserMenu}
                                        onMouseLeave={closeUserMenu}
                                    >
                                        <button
                                            type="button"
                                            className={styles.userSignedIconButton}
                                            aria-haspopup="true"
                                            aria-expanded={userMenuOpen}
                                        >
                                            <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                                                <path d="M12 12c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm0 2c-3.33 0-10 1.67-10 5v3h20v-3c0-3.33-6.67-5-10-5z" />
                                            </svg>
                                        </button>
                                        {userMenuOpen && (
                                            <div className={styles.userMenuDropdown}>
                                                <span className={styles.userMenuEmail}>{displayEmail}</span>
                                                <button
                                                    type="button"
                                                    className={styles.userMenuAvatarButton}
                                                    onClick={handleAvatarUploadClick}
                                                    aria-label="Upload avatar"
                                                >
                                                    {avatarUrl ? (
                                                        <img src={avatarUrl} alt="User avatar" className={styles.userMenuAvatarImage} />
                                                    ) : (
                                                        <span className={styles.userMenuAvatarInitial}>{avatarInitial}</span>
                                                    )}
                                                </button>
                                                <span className={styles.userMenuName}> Hi, {displayName}</span>
                                                <div className={styles.userMenuDivider} />
                                                <div className={styles.userMenuFooter}>
                                                    <SignOutButton>
                                                        <button type="button" className={styles.userMenuSignOutButton}>
                                                            Sign out
                                                        </button>
                                                    </SignOutButton>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </>
                            )}
                            <ButtonWithArrow
                                text="Get Started"
                                buttonColor="#0078d4"
                                textColor="#fff"
                                hoverTextColor="#fff"
                                hoverButtonColor="#015392"
                                isPrimaryAction
                                style={{
                                    visibility: isSmallScreen ? 'hidden' : 'visible',
                                    height: '38px',
                                    minHeight: '38px',
                                    margin: '0 10px',
                                    alignSelf: 'center',
                                    padding: '0 18px',
                                    fontSize: '1rem',
                                    borderRadius: '8px',
                                }}
                                isArrowAnimation={false}
                                onClick={() => setContactOpen(true)}
                            />
                        </div>
                    </div>
                )}
            </div>
            <input
                type="file"
                accept="image/*"
                ref={fileInputRef}
                onChange={handleAvatarFileChange}
                style={{ display: 'none' }}
            />
            {/* DrawerMenu (no header/logo) */}
            {/* Keep hamburgerOpen in sync with drawerOpen for edge cases (e.g. ESC key, overlay click) */}
            {isSmallScreen && (drawerOpen || drawerClosing) && <DrawerMenu />}
        </nav >
    );
};


export default React.memo(CustomNavbar);
